import type { ActionKeyT } from "../database/actions";
import { Bell, Calendar, Flag, Hammer, Hourglass, Mail } from "lucide-react";

export type ActionT = {
    name: string;
    definition: string;
    icon: React.ReactNode;
    email: boolean;
    explanation: string;
    patient_explainer: string;
    message?: string;
};

const reminderExplanation =
    "The patient has been sent an email. The alert status has been temporarily changed but will return if the patient remains inactive.";

export type ActionsT = {
    [K in ActionKeyT]: ActionT;
};

export const actions: ActionsT = {
    MARK_READ: {
        name: "Reviewed by doctor",
        definition: "Doctor has reviewed this diary.",
        icon: <Mail size={20} strokeWidth={1.5} />,
        email: true,
        explanation: reminderExplanation,
        patient_explainer: "Your diary has been reviewed by the doctor.",
    },
    REMIND: {
        name: "Remind a patient",
        definition: "Sends an email to the patient reminding them to log their pain.",
        icon: <Bell size={20} strokeWidth={1.5} />,
        email: true,
        explanation: reminderExplanation,
        patient_explainer: "You have been reminded to log your pain. Please ensure to update your diary.",
    },
    BOOK_FOLLOW_UP: {
        name: "Book Follow Up",
        definition:
            'Sends an email to the patient, CCing secretary to book a follow-up. Moves the diary into the "Scheduled" column.',
        icon: <Calendar size={20} strokeWidth={1.5} />,
        email: true,
        explanation:
            "An email has been sent to the patient. The diary secretary has been CCed and should arrange follow up. The diary has been moved to the Scheduling tab to be discharged when you see the patient.",
        patient_explainer:
            "An email has been sent to schedule a follow-up appointment. The diary is now in the Scheduling tab.",
    },
    BOOK_PROCEDURE: {
        name: "Book Procedure",
        definition:
            'Sends an email to the patient, CCing secretary to book a procedure. Moves the diary into the "Scheduled" column.',
        icon: <Hammer size={20} strokeWidth={1.5} />,
        email: true,
        explanation:
            "An email has been sent to the patient. The diary secretary has been CCed and should arrange the procedure. The diary has been moved to the Scheduling tab to be discharged when you see the patient.",
        patient_explainer: "An email has been sent to schedule a procedure. The diary is now in the Scheduling tab.",
    },
    DISCARD: {
        name: "Dismiss Alert",
        definition: "Dismisses the alert until another alert is triggered leaving the diary active.",
        icon: <Hourglass size={20} strokeWidth={1.5} />,
        email: false,
        message: 'Choosing "Dismiss Alert" will reset the alert until the patient\'s next log.',
        explanation: "The diary alert has been removed. The state has been changed to Active.",
        patient_explainer: "The diary alert has been dismissed, and the state is now Active.",
    },
    REINSTATE: {
        name: "Dismiss Alert",
        definition: 'Prolongs a "Scheduled" for two weeks.',
        icon: <Hourglass size={20} strokeWidth={1.5} />,
        email: false,
        message: "Dismiss Alert will reset alerts for two weeks while the patient continues to log.",
        explanation:
            "The diary alert has been changed to Silently Monitoring. When you see the patient, the diary should be discharged.",
        patient_explainer:
            "The diary alert has been changed to Silently Monitoring, and it will be active for two more weeks.",
    },
    DISCHARGE: {
        name: "Discharge Patient",
        definition:
            "Sends an email to the patient with an attached PDF record of the diary. Marks the diary as finished preventing from the patient entering further logs.",
        icon: <Flag size={20} strokeWidth={1.5} />,
        email: true,
        explanation: "The diary been has marked as Complete and moved to the Completed tab.",
        patient_explainer: "You have been discharged. The diary is marked as complete and moved to the Completed tab.",
    },
    CUSTOM: {
        name: "Remind the Patient",
        definition: "Sends an email to the patient from reminding them to log.",
        icon: <Bell size={20} strokeWidth={1.5} />,
        email: true,
        explanation: reminderExplanation,
        patient_explainer: "You have been reminded to log your information in the diary.",
    },
    MARK_UNREAD: {
        name: "Remind the Patient",
        definition: "Sends an email to the patient reminding them to register with The patient Watch.",
        icon: <Mail size={20} strokeWidth={1.5} />,
        email: true,
        explanation: reminderExplanation,
        patient_explainer: "You have been reminded to register with The Patient Watch.",
    },
    POST_UPDATE_MESSAGE: {
        name: "Message After Update",
        definition: "Send a message to the patient following a diary update.",
        icon: <Mail size={20} strokeWidth={1.5} />,
        email: true,
        explanation: "A message has been sent to the patient notifying them of updates to their diary.",
        patient_explainer: "Your diary has been updated. Please log in to review the latest changes.",
    },
};

export default actions;

export const actionKeys = Object.keys(actions);
